// Piezas comunes de los widgets del Inicio (rediseño oct-2026 + panel
// personalizable nov-2026): marco de cristal con botón "Ir", cifras que
// cuentan, barras en píldora, anillo animado y el catálogo de MÉTRICAS que
// los widgets configurables pueden pintar (histórico + proyectado).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, animate } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import { fmtCompact } from "@/components/ui/chart-kit";

export const nf0 = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0, useGrouping: "always" });
export const nf1 = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
export const nf2 = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const esNum = (v) => v !== null && v !== undefined && !Number.isNaN(v);

// Cifra corta para los KPIs grandes: 4,04 M€ · 546 k€ · 950 €
export function corto(v) {
  if (!esNum(v)) return "N/D";
  const a = Math.abs(v);
  if (a >= 1e6) return `${nf2.format(v / 1e6)} M€`;
  if (a >= 1e3) return `${nf0.format(v / 1e3)} k€`;
  return `${nf0.format(v)} €`;
}

// Formatos por unidad (mismo criterio que Comparativa)
export const FORMATOS = {
  eur: { corto, largo: (v) => (esNum(v) ? formatCurrency(v) : "N/D"), eje: fmtCompact },
  pct: { corto: (v) => (esNum(v) ? `${nf1.format(v)} %` : "N/D"), largo: (v) => (esNum(v) ? `${nf2.format(v)} %` : "N/D"), eje: (v) => `${nf0.format(v)}%` },
  x: { corto: (v) => (esNum(v) ? `${nf2.format(v)}x` : "N/D"), largo: (v) => (esNum(v) ? `${nf2.format(v)}x` : "N/D"), eje: (v) => `${nf1.format(v)}x` },
  veces: { corto: (v) => (esNum(v) ? nf2.format(v) : "N/D"), largo: (v) => (esNum(v) ? nf2.format(v) : "N/D"), eje: (v) => nf1.format(v) },
  dias: { corto: (v) => (esNum(v) ? `${nf0.format(v)} d` : "N/D"), largo: (v) => (esNum(v) ? `${nf0.format(v)} días` : "N/D"), eje: (v) => nf0.format(v) },
};

// Proyectado: fila del consolidado por concepto (misma fuente que Comparativa Años)
const pygProy = (concepto) => (p, a) =>
  (p?.pyg_completo || []).find((f) => f.concepto === concepto)?.por_anio?.[a];

// Métricas que pintan los widgets configurables. `hist` lee un año real de
// la comparativa (mismo shape que el dashboard); `proy` (si existe) lee un año
// proyectado del consolidado. Sin `proy` → el widget solo enseña histórico.
// `mejor`: "alto" | "bajo" para colorear la variación.
export const METRICAS = {
  ventas: { l: "Ventas", u: "eur", hist: (c) => c.kpis?.ventas, proy: (p, a) => p?.kpis?.ventas?.por_anio?.[a], mejor: "alto" },
  ebitda: { l: "EBITDA", u: "eur", hist: (c) => c.kpis?.ebitda, proy: (p, a) => p?.kpis?.ebitda?.por_anio?.[a], mejor: "alto" },
  margen_ebitda: { l: "Margen EBITDA", u: "pct", hist: (c) => c.kpis?.margen_ebitda, proy: (p, a) => p?.kpis?.ebitda?.margen_por_anio?.[a], mejor: "alto" },
  ebit: { l: "EBIT", u: "eur", hist: (c) => c.kpis?.ebit, proy: pygProy("EBIT"), mejor: "alto" },
  resultado_neto: { l: "Resultado neto", u: "eur", hist: (c) => c.kpis?.resultado_neto, proy: pygProy("Resultado neto"), mejor: "alto" },
  margen_neto: { l: "Margen neto", u: "pct", hist: (c) => c.kpis?.margen_neto, mejor: "alto" },
  cash_flow: { l: "Cash flow", u: "eur", hist: (c) => c.kpis?.cash_flow, mejor: "alto" },
  margen_bruto: { l: "Margen bruto", u: "eur", hist: (c) => c.ratios?.margen_bruto, mejor: "alto" },
  gastos_personal: { l: "Gastos de personal", u: "eur", hist: (c) => (esNum(c.pyg_resumen?.gastos_personal) ? Math.abs(c.pyg_resumen.gastos_personal) : null), mejor: "bajo" },
  fondo_maniobra: { l: "Fondo de maniobra", u: "eur", hist: (c) => c.ratios_liquidez?.fondo_maniobra, mejor: "alto" },
  nof: { l: "NOF", u: "eur", hist: (c) => c.ratios_liquidez?.nof, mejor: "bajo" },
  tesoreria: { l: "Tesorería", u: "eur", hist: (c) => c.balance_resumen?.tesoreria, mejor: "alto" },
  dfn: { l: "Deuda financiera neta", u: "eur", hist: (c) => c.balance_resumen?.dfn, mejor: "bajo" },
  patrimonio_neto: { l: "Patrimonio neto", u: "eur", hist: (c) => c.balance_resumen?.patrimonio_neto, mejor: "alto" },
  dfn_ebitda: { l: "DFN / EBITDA", u: "x", hist: (c) => c.ratios_endeudamiento?.dfn_ebitda, mejor: "bajo" },
  liquidez: { l: "Liquidez corriente", u: "veces", hist: (c) => c.ratios_liquidez?.liquidez_principal, mejor: "alto" },
  solvencia: { l: "Solvencia", u: "veces", hist: (c) => c.ratios_solvencia?.solvencia_principal, mejor: "alto" },
  roe: { l: "ROE", u: "pct", hist: (c) => c.ratios?.roe, mejor: "alto" },
  roa: { l: "ROA", u: "pct", hist: (c) => c.ratios?.roa, mejor: "alto" },
  plazo_cobro: { l: "Plazo de cobro", u: "dias", hist: (c) => c.ratios_rotacion?.plazo_cobro, mejor: "bajo" },
  plazo_pago: { l: "Plazo de pago", u: "dias", hist: (c) => c.ratios_rotacion?.plazo_pago, mejor: "alto" },
};

export const OPCIONES_METRICA = Object.entries(METRICAS).map(([v, m]) => ({ v, l: m.l }));
export const OPCIONES_METRICA_EUR = OPCIONES_METRICA.filter((o) => METRICAS[o.v].u === "eur");

// Serie año a año de una métrica: reales de la comparativa + proyectados del
// consolidado (en el año que es real y proyectado gana el real).
export function serieMetrica(clave, { comp, proy }, conProyeccion = true) {
  const m = METRICAS[clave] || METRICAS.ventas;
  const reales = comp.map((c) => ({ anio: c.anio, valor: m.hist(c) ?? null, proy: false }));
  if (!conProyeccion || !m.proy) return reales;
  const vistos = new Set(comp.map((c) => c.anio));
  const futuros = (proy?.anios_proyectados || [])
    .filter((a) => !vistos.has(a))
    .map((a) => ({ anio: a, valor: m.proy(proy, String(a)) ?? null, proy: true }));
  return [...reales, ...futuros];
}

// Número que "cuenta" hasta su valor al aparecer
export function Contador({ valor, formato = corto, className }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!esNum(valor)) return undefined;
    const c = animate(0, valor, { duration: 1.1, ease: [0.16, 1, 0.3, 1], onUpdate: setV });
    return () => c.stop();
  }, [valor]);
  return <span className={className}>{esNum(valor) ? formato(v) : "N/D"}</span>;
}

// Marco de cristal de cada widget, con botón "Ir" a su pantalla
export function Marco({ to, titulo, subtitulo, icono: Icono, oscuro, className, children, testId, extra }) {
  return (
    <div
      className={cn("group relative flex h-full min-h-0 flex-col overflow-hidden rounded-[1.5rem] p-4", oscuro ? "glass-ink" : "glass", className)}
      data-testid={testId}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={cn("flex items-center gap-1.5 text-[13px] font-semibold", oscuro && "text-white")}>
            {Icono && <Icono className={cn("h-3.5 w-3.5 shrink-0", oscuro ? "text-primary" : "text-muted-foreground")} />}
            <span className="truncate">{titulo}</span>
          </div>
          {subtitulo && <div className={cn("truncate text-[11px]", oscuro ? "text-white/55" : "text-muted-foreground")}>{subtitulo}</div>}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {extra}
          {to && (
            <Link
              to={to}
              className={cn(
                "inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-[11px] font-medium transition-colors",
                oscuro
                  ? "bg-white/10 text-white hover:bg-primary hover:text-[hsl(240_11%_18%)]"
                  : "bg-foreground/[0.06] text-foreground hover:bg-[hsl(var(--pill-active))] hover:text-[hsl(var(--pill-active-foreground))]"
              )}
            >
              Ir <ArrowUpRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </div>
  );
}

// Barras verticales en píldora: gunmetal, el año destacado en amarillo y
// los proyectados rayados en amarillo corporativo.
export function BarrasPildora({ datos, destacado, formato = fmtCompact }) {
  const max = Math.max(...datos.map((d) => Math.abs(d.valor || 0)), 1);
  return (
    <div className="flex min-h-0 flex-1 items-end justify-between gap-2 pt-2">
      {datos.map((d, i) => {
        const alto = Math.max(8, (Math.abs(d.valor || 0) / max) * 100);
        const esDest = d.anio === destacado;
        return (
          <div key={d.anio} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1.5" title={`${d.anio}${d.proy ? " (proyectado)" : ""}: ${esNum(d.valor) ? formato(d.valor) : "N/D"}`}>
            <span className="text-[10px] tabular-nums text-muted-foreground">{esNum(d.valor) ? formato(d.valor) : "—"}</span>
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${alto}%` }}
              transition={{ delay: 0.15 + i * 0.05, type: "spring", stiffness: 120, damping: 18 }}
              className={cn(
                "w-full max-w-[22px] rounded-full",
                esDest ? "bg-primary shadow-[0_6px_18px_-6px_hsl(var(--primary)/0.8)]" : d.proy ? "" : "bg-[hsl(var(--pill-active))] dark:bg-white/80",
                d.valor < 0 && !esDest && !d.proy && "bg-rose-400 dark:bg-rose-400"
              )}
              style={d.proy && !esDest ? {
                backgroundImage: "repeating-linear-gradient(135deg, hsl(var(--proy) / 0.75) 0 4px, hsl(var(--proy) / 0.25) 4px 8px)",
              } : undefined}
            />
            <span className={cn("text-[10px] tabular-nums", d.proy ? "text-proy" : "text-muted-foreground")}>{String(d.anio).slice(-2)}</span>
          </div>
        );
      })}
    </div>
  );
}

// Anillo de progreso animado (porcentaje 0-100)
export function Anillo({ pct, etiqueta, color = "hsl(var(--primary))", oscuro, formato }) {
  const r = 42;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, esNum(pct) ? pct : 0));
  return (
    <div className="relative mx-auto aspect-square h-full max-h-[150px] min-h-[72px]">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" strokeWidth="9" className={oscuro ? "stroke-white/10" : "stroke-foreground/10"} />
        <motion.circle
          cx="50" cy="50" r={r} fill="none" strokeWidth="9" strokeLinecap="round" stroke={color}
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c * (1 - v / 100) }}
          transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <Contador valor={pct} formato={formato || ((x) => `${nf1.format(x)} %`)} className={cn("text-lg font-semibold tabular-nums", oscuro && "text-white")} />
        <span className={cn("text-[10px]", oscuro ? "text-white/55" : "text-muted-foreground")}>{etiqueta}</span>
      </div>
    </div>
  );
}

// Barra horizontal animada (0-100 %)
export function Barra({ pct, className, delay = 0.3 }) {
  return (
    <div className="h-2.5 overflow-hidden rounded-full bg-foreground/10">
      <motion.div
        className={cn("h-full rounded-full bg-[hsl(var(--pill-active))]", className)}
        initial={{ width: 0 }}
        animate={{ width: `${Math.max(0, Math.min(100, pct || 0))}%` }}
        transition={{ delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      />
    </div>
  );
}

export const SEMAFORO = { ok: "bg-emerald-400", warning: "bg-amber-400", error: "bg-rose-400" };
